/**
 * Esquemas del módulo **Profesores, liquidación y comisiones** (AF §7).
 *
 * Fuente única entre backend, SPA (áreas `admin/` y `profes/`) y app. Todo lo que la
 * pantalla manda y todo lo que la API devuelve pasa por acá.
 *
 * Dos reglas que están EN LA FORMA del dato, no en un comentario:
 * - **RN-PRO-03**: ningún esquema de este archivo describe un cobro, una caja ni una
 *   venta. El portal profesores no tiene por dónde tocar plata que no sea la suya.
 * - **RN-PRO-21**: `teacherTrialItem` no tiene teléfono ni email. El profe ve el nombre,
 *   la edad y la actividad de interés; el contacto no existe en la respuesta.
 */
import { z } from "zod";
import { EXTERNAL_INVOICE_STATUSES, HIRE_MODES, PAYROLL_LINE_KINDS, PAYROLL_SETTLEMENT_STATUSES, STAFF_STATUSES, STAFF_TYPES, TIMECLOCK_RESOLUTIONS, WORK_SHIFT_KINDS, } from "../enums.js";
import { dateISO, dateOnly, listQuery, moneySchema, objectId, pagination, reasonRequired, timeHHmm, yearMonth, } from "./base.js";
// ─────────────────────────────────────────────────────────────────────────────
// 7.1 Plantel y legajos (RN-PRO-01..03)
// ─────────────────────────────────────────────────────────────────────────────
/** RN-PRO-04: la tarifa individual PISA la de la categoría, campo por campo. */
export const individualRates = z.object({
    salaHour: moneySchema.min(0).optional(),
    classHour: moneySchema.min(0).optional(),
    premiumClassHour: moneySchema.min(0).optional(),
});
export const createStaffRecordBody = z.object({
    userId: objectId,
    type: z.enum(STAFF_TYPES),
    hireMode: z.enum(HIRE_MODES),
    baseBranchId: objectId,
    rateCategoryCode: z.string().trim().min(1).max(40),
    individualRates: individualRates.optional(),
    cuitCuil: z
        .string()
        .trim()
        .regex(/^\d{2}-?\d{8}-?\d$/, "CUIT/CUIL esperado como 20-12345678-9")
        .optional(),
    startedAt: dateISO,
});
/**
 * RN-PRO-01: cambiar tipo, modalidad, sede base o tarifa mueve plata en la próxima
 * liquidación, así que el motivo es obligatorio y queda auditado (D5, pantalla «Personal
 * y legajos»). `personalTrainingEnabled` NO se edita a mano: es materializado (RN-PRO-22).
 */
export const updateStaffRecordBody = z
    .object({
    type: z.enum(STAFF_TYPES).optional(),
    hireMode: z.enum(HIRE_MODES).optional(),
    baseBranchId: objectId.optional(),
    rateCategoryCode: z.string().trim().min(1).max(40).optional(),
    individualRates: individualRates.nullable().optional(),
    cuitCuil: z.string().trim().max(20).optional(),
    status: z.enum(STAFF_STATUSES).optional(),
    reason: reasonRequired,
})
    .refine((v) => Object.keys(v).some((k) => k !== "reason"), "No hay nada para cambiar en el legajo");
export const listStaffQuery = listQuery.extend({
    type: z.enum(STAFF_TYPES).optional(),
    hireMode: z.enum(HIRE_MODES).optional(),
    status: z.enum(STAFF_STATUSES).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 7.2 Tarifario y multiplicadores (RN-PRO-04/05)
// ─────────────────────────────────────────────────────────────────────────────
export const rateCategory = z.object({
    code: z.string().trim().min(1).max(40),
    /** rel. 6.12: la sala se paga MENOS que la clase. */
    salaHour: moneySchema.min(0),
    classHour: moneySchema.min(0),
    premiumClassHour: moneySchema.min(0),
});
/** RN-PRO-05: no se acumulan — un feriado en domingo aplica el MAYOR (PV-54). */
export const rateMultipliers = z.object({
    sunday: z.number().positive().max(10),
    holiday: z.number().positive().max(10),
    saturday: z.number().positive().max(10),
});
/** RN-PRO-04: versión nueva con vigencia. Nunca se edita una versión publicada. */
export const createRateCardBody = z.object({
    effectiveFrom: dateISO,
    categories: z.array(rateCategory).min(1),
    multipliers: rateMultipliers.optional(),
});
export const listRateCardsQuery = pagination;
/** El calendario de feriados es administrable (RN-PRO-05). */
export const setHolidaysBody = z.object({
    year: z.string().regex(/^\d{4}$/, "Año esperado YYYY"),
    dates: z.array(dateOnly),
});
export const rateQuoteQuery = z.object({
    staffId: objectId,
    date: dateISO,
    kind: z.enum(["sala", "class", "reception"]),
    awarded: z
        .enum(["true", "false"])
        .optional()
        .transform((v) => v === "true"),
});
// ─────────────────────────────────────────────────────────────────────────────
// 7.3 Turnos y fichajes (RN-PRO-06..08)
// ─────────────────────────────────────────────────────────────────────────────
export const createWorkShiftBody = z
    .object({
    branchId: objectId,
    staffId: objectId,
    kind: z.enum(WORK_SHIFT_KINDS),
    date: dateISO,
    plannedStart: timeHHmm,
    plannedEnd: timeHHmm,
    /** «sábado por medio», «domingo Martínez»: modela la rotación real (RN-PRO-07). */
    rotationTag: z.string().trim().max(60).optional(),
})
    .refine((v) => v.plannedEnd > v.plannedStart, "El turno termina antes de empezar");
/** Alta en lote: la grilla de un mes se arma de una, no turno por turno. */
export const createWorkShiftsBody = z.object({
    shifts: z.array(createWorkShiftBody).min(1).max(500),
});
export const updateWorkShiftBody = z.object({
    plannedStart: timeHHmm.optional(),
    plannedEnd: timeHHmm.optional(),
    rotationTag: z.string().trim().max(60).optional(),
    staffId: objectId.optional(),
});
export const listWorkShiftsQuery = pagination.extend({
    branchId: objectId.optional(),
    staffId: objectId.optional(),
    kind: z.enum(WORK_SHIFT_KINDS).optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    month: yearMonth.optional(),
});
export const timeclockReviewQuery = z.object({
    staffId: objectId.optional(),
    branchId: objectId.optional(),
    month: yearMonth,
});
/**
 * RN-PRO-08: la discrepancia la resuelve el coordinador **con motivo** (D5).
 * `adjust` pide los minutos que efectivamente se liquidan: aprobar una hora extra o
 * recortar una llegada tarde son la misma acción con distinto número.
 */
export const resolveTimeclockBody = z
    .object({
    resolution: z.enum(TIMECLOCK_RESOLUTIONS),
    minutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .optional(),
    reason: reasonRequired,
})
    .refine((v) => v.resolution !== "adjust" || v.minutes !== undefined, "Un ajuste necesita los minutos que se liquidan");
/** RN-ACC-23: la fichada olvidada se corrige con una entrada NUEVA vinculada. */
export const correctTimeclockBody = z.object({
    ofEntryId: objectId.optional(),
    staffId: objectId,
    locationId: objectId,
    direction: z.enum(["in", "out"]),
    ts: dateISO,
    reason: reasonRequired,
});
// ─────────────────────────────────────────────────────────────────────────────
// 7.5 Vara móvil (RN-PRO-12..15)
// ─────────────────────────────────────────────────────────────────────────────
export const listPerformanceBarsQuery = pagination.extend({
    teacherId: objectId.optional(),
    branchId: objectId.optional(),
    disciplineId: objectId.optional(),
});
/** RN-PRO-15: ajuste manual del coordinador — motivo obligatorio, auditado (D5). */
export const adjustPerformanceBarBody = z.object({
    bar: z.number().int().min(0).max(500),
    reason: reasonRequired,
});
// ─────────────────────────────────────────────────────────────────────────────
// 7.6 Reemplazos y clases sin alumnos (RN-PRO-16..18)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * RN-PRO-16: el reemplazo se carga ANTES de liquidar y cobra quien dictó.
 * `substituteTeacherId: null` deshace el reemplazo (el titular vuelve a cobrar).
 */
export const setSubstituteBody = z.object({
    substituteTeacherId: objectId.nullable(),
    reason: reasonRequired,
});
/** RN-PRO-17: la valida el coordinador en la pre-liquidación. */
export const validateNoStudentsBody = z.object({
    approve: z.boolean(),
    reason: reasonRequired,
});
// ─────────────────────────────────────────────────────────────────────────────
// 7.9 Comisiones de recepción (RN-PRO-23..27)
// ─────────────────────────────────────────────────────────────────────────────
export const commissionTierSchema = z.object({
    thresholdPct: z.number().min(0).max(1000),
    commissionPct: z.number().min(0).max(100),
});
/** RN-PRO-23: lo cargan Administración o Dueño a principio de mes. */
export const upsertReceptionGoalBody = z.object({
    userId: objectId,
    month: yearMonth,
    branchId: objectId,
    targetAmount: moneySchema.min(1),
    /** Después del día `payroll.goalLockDay` el motivo es obligatorio (D5). */
    reason: z.string().trim().min(5).max(500).optional(),
});
export const listReceptionGoalsQuery = pagination.extend({
    month: yearMonth,
    branchId: objectId.optional(),
    userId: objectId.optional(),
});
/** RN-PRO-24: la venta se reasigna con motivo; la comisión sigue al vendedor. */
export const reassignSaleBody = z.object({
    paymentId: objectId,
    toUserId: objectId,
    reason: reasonRequired,
});
export const myCommissionQuery = z.object({ month: yearMonth.optional() });
// ─────────────────────────────────────────────────────────────────────────────
// 7.10 Liquidación mensual (RN-PRO-28..31)
// ─────────────────────────────────────────────────────────────────────────────
export const preparePayrollBody = z.object({
    month: yearMonth,
    staffId: objectId.optional(),
    branchId: objectId.optional(),
});
export const listPayrollSettlementsQuery = pagination.extend({
    month: yearMonth.optional(),
    branchId: objectId.optional(),
    staffId: objectId.optional(),
    status: z.enum(PAYROLL_SETTLEMENT_STATUSES).optional(),
    hireMode: z.enum(HIRE_MODES).optional(),
});
/** RN-PRO-29: el encargado ajusta cualquier renglón con motivo obligatorio (D5). */
export const adjustPayrollLineBody = z
    .object({
    amount: moneySchema.optional(),
    qty: z.number().min(0).max(1000).optional(),
    awarded: z.boolean().optional(),
    notes: z.string().trim().max(300).optional(),
    reason: reasonRequired,
})
    .refine((v) => v.amount !== undefined || v.qty !== undefined || v.awarded !== undefined, "Un ajuste tiene que cambiar el importe, la cantidad o el premio");
/** Renglón que el coordinador agrega a mano (p. ej. el premio de una clase master). */
export const addPayrollLineBody = z.object({
    kind: z.enum(PAYROLL_LINE_KINDS),
    date: dateISO.optional(),
    sessionId: objectId.optional(),
    qty: z.number().min(0).max(1000).default(1),
    amount: moneySchema,
    notes: z.string().trim().max(300).optional(),
    reason: reasonRequired,
});
export const closePayrollBody = z.object({
    /** El cierre no es una excepción: es el circuito. El motivo queda opcional. */
    note: z.string().trim().max(300).optional(),
});
export const externalInvoiceBody = z.object({
    status: z.enum(EXTERNAL_INVOICE_STATUSES),
});
/**
 * RN-PRO-29: una liquidación CERRADA no se reabre. El error se arrastra al mes
 * siguiente como `retro_adjustment`, con su motivo y su origen.
 */
export const retroAdjustBody = z.object({
    amount: moneySchema,
    reason: reasonRequired,
});
export const payrollExportQuery = z.object({
    month: yearMonth,
    branchId: objectId.optional(),
    hireMode: z.enum(HIRE_MODES).optional(),
    format: z.enum(["csv", "xlsx"]).default("csv"),
});
// ─────────────────────────────────────────────────────────────────────────────
// Sobre de pago a profes (RN-PRO-32)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * RN-PRO-32: el sobre lo arman los dueños por fuera de la caja; el sistema sólo registra
 * que está listo y que se entregó. No hay estado «con diferencia»: los montos no se validan.
 */
export const PAYROLL_PAYOUT_STATUSES = ["pending", "delivered"];
/** Acción masiva: un sobre por cada liquidación cerrada del mes que todavía no lo tenga. */
export const markPayoutsReadyBody = z.object({
    month: yearMonth,
    branchId: objectId.optional(),
});
export const listPayrollPayoutsQuery = pagination.extend({
    month: yearMonth.optional(),
    status: z.enum(PAYROLL_PAYOUT_STATUSES).optional(),
    staffUserId: objectId.optional(),
    branchId: objectId.optional(),
});
/** El código de 6 dígitos que el profe recibió por email y le dicta a la recepcionista. */
export const deliverPayoutBody = z.object({
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, "El código tiene 6 dígitos"),
});
// ─────────────────────────────────────────────────────────────────────────────
// Portal profesores (RN-PRO-03/15/21/26/31)
// ─────────────────────────────────────────────────────────────────────────────
export const myMonthQuery = z.object({ month: yearMonth.optional() });
export const myClassesQuery = z.object({
    from: dateISO.optional(),
    to: dateISO.optional(),
    month: yearMonth.optional(),
});
/** RN-PRO-17: el profe marca la clase con 0 presentes dentro de la ventana. */
export const markNoStudentsBody = z.object({
    note: z.string().trim().max(300).optional(),
});
/** AF §7.11 «Mis clases»: avisar ausencia dispara el aviso al coordinador (D3). */
export const reportAbsenceBody = z.object({
    reason: z.string().trim().min(5).max(500),
});
/**
 * RN-PRO-21: lo ÚNICO que el profe ve de un probador. Sin teléfono, sin email —
 * la regla vive en la forma del dato, no en un `if` que alguien puede olvidar.
 */
export const teacherTrialItem = z.object({
    id: objectId,
    name: z.string(),
    age: z.number().int().nullable(),
    disciplineName: z.string().nullable(),
    startsAt: dateISO,
    attended: z.boolean().nullable(),
    converted: z.boolean(),
});
//# sourceMappingURL=profesores.js.map